import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AttributeValueEditor } from "../../components/attribute/attributeValueEditor";
import { Panel, PanelBody } from "../../components/panel/panel";
import Partner from "../../models/partner";
import EntityTypes from "../../models/entityTypes";
import { api } from "../../store/api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectLabels } from "../../store/labelsSlice";
import { fetchAttributes, selectConfig } from "../../store/configSlice";
import { PartnersRoute } from "./partnersScreen";
import { setTitle } from "../../util/useDocumentTitle";

export const PartnerEditRoute = "/partners/edit/:id";

interface PartnerEditScreenParams {
  id?: string;
}

export default function PartnerEditScreen() {
  const navigate = useNavigate();
  const labels = useAppSelector(selectLabels);
  const label = labels.partner;
  const entityApi = api.partners;

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultValue, setDefaultValue] = useState<Partner>();
  const [error, setError] = useState<String>();
  const params = useParams<keyof PartnerEditScreenParams>();

  const dispatch = useAppDispatch();
  const config = useAppSelector(selectConfig);
  const attributes = config.attributes[EntityTypes.Partner];
  useEffect(() => { dispatch(fetchAttributes(EntityTypes.Partner)) }, [dispatch]);

  const form = useForm<Partner>();
  const { register, handleSubmit, reset, formState: { errors }, control } = form;
  const { fields, replace } = useFieldArray({
    name: "attributes",
    keyName: "attributeId",
    control
  });

  const onSubmit: SubmitHandler<Partner> = data => {
    console.log(data);
    const request: Partner = {
      name: data.name,
      isActive: data.isActive,
      id: data.id,
      attributes: data.attributes?.map((attrib) => {
        return {
          id: attrib.id,
          boolValue: attrib.boolValue,
          dateValue: attrib.dateValue,
          intValue: attrib.intValue,
          stringValue: attrib.stringValue,
          choicesValue: (attrib.choicesValue ?
            (Array.isArray(attrib.choicesValue) ? attrib.choicesValue : [attrib.choicesValue])
            : undefined
          ),
        };
      })
    };
    console.log(request);
    setIsLoading(true);
    (isEditing ? entityApi.update(request) : entityApi.create(request))
      .then((record) => {
        navigate(PartnersRoute);
        setIsLoading(false);
      })
      .catch((reason) => {
        setError("Unable to load " + label.singular + ": " + reason);
        setIsLoading(false);
      });
  };


  // Setup form fields
  useEffect(() => {
    if (params.id) {
      setIsEditing(true);
      setIsLoading(true);
      entityApi.get(parseInt(params.id!))
        .then((record) => {
          setDefaultValue(record);
          setIsLoading(false);
        })
        .catch((reason) => {
          console.error(reason);
          setError("Unable to load " + label.singular + ` #${params.id}`);
        });
    }
    else {
      setDefaultValue({
        isActive: true,
        name: '',
      });
    }
  }, [entityApi, label.singular, params.id]);
  
  // Initialize Form with Attributes and defaultValue
  useEffect(() => {
    if (attributes && defaultValue) {
      reset({
        ...defaultValue,
        attributes: attributes.map((attrib) => {
          if (defaultValue?.attributes) {
            const attributeValue = defaultValue?.attributes?.find((t) => t?.id === attrib.id);
            if (attributeValue) {
              // console.warn('replacing field', attributeValue);
              return attributeValue;
            }
          }
          return {
            id: attrib.id,
          }
        }),
      }, {

      });
    }
  }, [attributes, replace, defaultValue, reset]);

  const watchAttributes = useWatch({
    control: control,
    name: `attributes`
  });


  // Title
  useEffect(() => {
    setTitle([
      `${isEditing ? 'Edit' : 'Create'} ${labels.partner.singular}`,
      defaultValue?.name,
    ]);
  }, [defaultValue?.name, isEditing, labels.partner.singular]);

  return (<div>
    <ol className="breadcrumb float-xl-end">
      <li className="breadcrumb-item"><Link to="..">{label.plural}</Link></li>
      {isEditing && <li className="breadcrumb-item active">Edit</li>}
      {!isEditing && <li className="breadcrumb-item active">Create</li>}
    </ol>
    <h1 className="page-header">{label.singular} Details</h1>
    <div className="row">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Panel className="card border-0" isLoading={isLoading}>
            <PanelBody className="card-body">
              {error && <div className="alert alert-danger"><strong>Error!</strong> {error}</div>}
              <div className="row mb-15px">
                <label className="form-label col-form-label col-md-3">Name</label>
                <div className="col-md-9">
                  <input type="text" className={"form-control mb-5px " + (errors.name ? 'is-invalid' : '')} {...register("name", { required: true })} />
                  {errors.name && <div className="invalid-feedback">This field is required</div>}
                </div>
              </div>

              {fields?.map((field, index) => {
                const attribute = attributes?.find(attrib => attrib.id === field.id);
                if (attribute) {
                  //console.log('field', attribute.name, field);
                  return (<div key={field.id} className={"row mb-15px" + (attribute.isRequired ? " required" : "") }>
                    <label className="form-label col-form-label col-md-3">{attribute.name}</label>
                    <div className="col-md-9">
                      <AttributeValueEditor attribute={attribute} errors={errors} index={index} watch={watchAttributes} />
                    </div>
                  </div>
                  );
                }
                return <div key={field.id}></div>;
              }
              )}

              <div className="row mb-15px">
                <label className="form-label col-form-label col-md-3">Status</label>
                <div className="col-md-9 pt-2">
                  <div className="form-check form-switch mb-2">
                    <input className="form-check-input" type="checkbox" id="isActive" {...register("isActive")} />
                    <label className="form-check-label" htmlFor="isActive">Active</label>
                  </div>
                </div>
              </div>

              <div className="row mb-0">
                <div className="offset-md-3 col-md-9 pt-2">
                  <button type="submit" className="btn btn-primary w-100px me-5px">Save</button>
                  <button type="button" onClick={() => navigate(-1)} className="btn btn-default w-100px">Cancel</button>
                </div>
              </div>
            </PanelBody>
          </Panel>
        </form>
      </FormProvider>
    </div>
  </div>);
}
